import type { ErrorVm } from './types'

/**
 * Управление ценами базы 1С из окна «Прайс 1С» (замечание владельца 17.10.2026): создать запись цены и удалить запись —
 * как в 1С. Черновик формы — намерение человека на время экрана; ответы 1С в нём не хранятся.
 */
export interface PriceDraft {
  /** Текст поиска товара. */
  productSearch: string
  product: { key: string; label: string } | null
  priceType: string
  priceText: string
  /** Дата записи `YYYY-MM-DD`; пусто — сегодня. */
  period: string
}

export const EMPTY_PRICE_DRAFT: PriceDraft = { productSearch: '', product: null, priceType: '', priceText: '', period: '' }

/** Цена из текста: неотрицательное число, точка или запятая, не более двух знаков. */
export function parsePrice(text: string): number | null {
  const normalized = text.trim().replace(',', '.')
  return /^\d{1,12}(\.\d{1,2})?$/.test(normalized) ? Number(normalized) : null
}

const DAY = /^\d{4}-\d{2}-\d{2}$/

export function isRealDay(value: string): boolean {
  if (!DAY.test(value)) return false
  const parsed = new Date(`${value}T00:00:00Z`)
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().startsWith(value)
}

/** Чего не хватает форме; `null` — можно сохранять. Слова человеку, не коды. */
export function draftProblem(draft: PriceDraft): string | null {
  if (draft.product === null) return 'Выберите товар'
  if (draft.priceType === '') return 'Выберите вид цен'
  if (parsePrice(draft.priceText) === null) return 'Цена — число не меньше нуля, не более двух знаков после запятой'
  if (draft.period !== '' && !isRealDay(draft.period)) return 'Дата — вида ГГГГ-ММ-ДД'
  return null
}

export interface ProductChoiceVm {
  key: string
  label: string
}

export interface ProductSearchVm {
  status: 'idle' | 'loading' | 'empty' | 'error' | 'ready'
  items: readonly ProductChoiceVm[]
  message: string | null
}

export interface PriceManageVm {
  open: boolean
  draft: PriceDraft
  problem: string | null
  products: ProductSearchVm
  saving: boolean
  error: ErrorVm | null
  /** «Цена записана» / «В 1С уже стоит эта цена» после удачного сохранения. */
  done: string | null
  remove: {
    /** Строка списка, для которой ждём подтверждения. */
    confirmKey: string | null
    busy: boolean
    error: ErrorVm | null
    done: string | null
  }
}

export interface PriceManageActions {
  openForm: () => void
  closeForm: () => void
  productSearchChanged: (text: string) => void
  productChosen: (product: ProductChoiceVm) => void
  productCleared: () => void
  priceTypeChanged: (key: string) => void
  priceChanged: (text: string) => void
  periodChanged: (day: string) => void
  save: () => void
  /** `rowKey` — ключ строки списка (товар × вид цен). */
  askRemove: (rowKey: string) => void
  cancelRemove: () => void
  confirmRemove: () => void
}
